import React, { useState, useRef, useEffect } from 'react';
import {
  X,
  Sparkles,
  Send,
  Calculator,
  AlertTriangle,
  BookOpen,
  ArrowRight,
  Bot,
  User as UserIcon
} from 'lucide-react';
import { useGst } from '../context/GstContext';
import { searchGstKnowledgeBase } from '../services/complianceRagService';
import { runGstr2bReconciliation } from '../services/reconciliationService';
import Decimal from 'decimal.js';

interface Message {
  id: string;
  sender: 'user' | 'assistant';
  text: string;
  toolCall?: {
    toolName: string;
    result: any;
  };
  timestamp: string;
}

export const ScopedAiCopilotDrawer: React.FC = () => {
  const {
    copilotOpen,
    setCopilotOpen,
    clientId,
    clientLegalName,
    financialYear,
    taxPeriod,
    invoices,
    gstr2bRecords,
    themeMode
  } = useGst();
  const isLight = themeMode === 'light';

  const [input, setInput] = useState<string>('');
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      sender: 'assistant',
      text: `Hello! I am your Indian GST Compliance Copilot for ${clientLegalName}. I am strictly scoped to your active period (${taxPeriod} ${financialYear}) and use deterministic tools to evaluate taxes, mismatches, and statutory CGST sections. How can I help you today?`,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  ]);
  const [isThinking, setIsThinking] = useState<boolean>(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isThinking]);

  // TOOL 1: Deterministic Tax Summary
  const getDeterministicTaxSummary = () => {
    const sales = invoices.filter(i => i.category === 'B2B_SALES' || i.category === 'B2C_RETAIL');
    const purchases = invoices.filter(i => i.category === 'B2B_PURCHASES' || i.category === 'EXPENSE_RECEIPT');

    let totalSalesTaxable = new Decimal(0);
    let totalOutputTax = new Decimal(0);
    for (const s of sales) {
      totalSalesTaxable = totalSalesTaxable.plus(s.total_taxable_value || 0);
      totalOutputTax = totalOutputTax.plus(s.total_tax || 0);
    }

    const recon = runGstr2bReconciliation(purchases, gstr2bRecords);

    return {
      clientId,
      financialYear,
      taxPeriod,
      totalSalesCount: sales.length,
      totalSalesTaxable: totalSalesTaxable.toDecimalPlaces(2).toNumber(),
      totalOutputTax: totalOutputTax.toDecimalPlaces(2).toNumber(),
      totalPurchaseCount: purchases.length,
      totalPurchaseItcInBooks: recon.summary.totalPurchaseItc,
      matchedItcIn2B: recon.summary.matchedItc,
      missingItcAtRisk: recon.summary.missingIn2bItc,
      blockedItcSec17_5: recon.summary.blockedItc,
      rcmItc: recon.summary.rcmItc,
      netTaxPayableEst: Math.max(0, totalOutputTax.minus(recon.summary.matchedItc).toDecimalPlaces(2).toNumber())
    };
  };

  // TOOL 2: Flagged Invoices & Mismatches
  const getFlaggedInvoicesAndMismatches = () => {
    const mathFailures = invoices.filter(i => !i.math_verified);
    const blockedCredits = invoices.filter(i => i.itc_eligibility === 'BLOCKED_17_5');
    const rcmInvoices = invoices.filter(i => i.itc_eligibility === 'RCM_APPLICABLE');
    const purchases = invoices.filter(i => i.category === 'B2B_PURCHASES' || i.category === 'EXPENSE_RECEIPT');
    const recon = runGstr2bReconciliation(purchases, gstr2bRecords);
    const missingIn2B = recon.results.filter(r => r.reconStatus === 'MISSING_IN_2B');

    return {
      mathFailures: mathFailures.map(i => ({
        invoice_number: i.invoice_number,
        supplier_name: i.supplier_name,
        stated_total: i.invoice_total,
        total_tax: i.total_tax
      })),
      missingIn2B: missingIn2B.map(m => ({
        invoice_number: m.invoice.invoice_number,
        supplier_gstin: m.invoice.supplier_gstin,
        supplier_name: m.invoice.supplier_name,
        tax_at_risk: m.invoice.total_tax
      })),
      blockedCredits: blockedCredits.map(b => ({
        invoice_number: b.invoice_number,
        supplier_name: b.supplier_name,
        blocked_tax: b.total_tax,
        note: b.ai_compliance_note
      })),
      rcmInvoices: rcmInvoices.map(r => ({
        invoice_number: r.invoice_number,
        supplier_name: r.supplier_name,
        tax: r.total_tax,
        note: r.ai_compliance_note
      }))
    };
  };

  // TOOL 3: Indian GST Law Knowledge Base Search
  const searchLaw = (query: string) => {
    return searchGstKnowledgeBase(query);
  };

  const handleSend = async (textToSend?: string) => {
    const q = (textToSend || input).trim();
    if (!q) return;

    const userMsg: Message = {
      id: `user_${Date.now()}`,
      sender: 'user',
      text: q,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsThinking(true);

    setTimeout(() => {
      const qLower = q.toLowerCase();
      let responseText = "";
      let toolCall: Message['toolCall'] = undefined;

      if (qLower.includes("summary") || qLower.includes("tax payable") || qLower.includes("gstr-3b") || qLower.includes("numbers") || qLower.includes("total")) {
        const summary = getDeterministicTaxSummary();
        toolCall = {
          toolName: "getDeterministicTaxSummary",
          result: summary
        };
        responseText = `Here is your deterministic tax calculation for **${taxPeriod} ${financialYear}**:\n\n` +
          `• **Total Outward Sales Taxable:** ₹${summary.totalSalesTaxable.toLocaleString('en-IN')}\n` +
          `• **Total Output Tax Payable:** ₹${summary.totalOutputTax.toLocaleString('en-IN')}\n` +
          `• **Eligible ITC Matched in GSTR-2B:** ₹${summary.matchedItcIn2B.toLocaleString('en-IN')}\n` +
          `• **ITC At Risk (Missing in 2B):** ₹${summary.missingItcAtRisk.toLocaleString('en-IN')}\n` +
          `• **Blocked under Sec 17(5):** ₹${summary.blockedItcSec17_5.toLocaleString('en-IN')}\n` +
          `• **Reverse Charge (RCM) Invoices:** ₹${summary.rcmItc.toLocaleString('en-IN')}\n\n` +
          `**Estimated Net Cash Tax Payable:** ₹${summary.netTaxPayableEst.toLocaleString('en-IN')}`;
      } else if (qLower.includes("mismatch") || qLower.includes("flagged") || qLower.includes("risk") || qLower.includes("missing")) {
        const flagged = getFlaggedInvoicesAndMismatches();
        toolCall = {
          toolName: "getFlaggedInvoicesAndMismatches",
          result: flagged
        };
        responseText = `Audit scan completed for **${taxPeriod} ${financialYear}**:\n\n` +
          `• **${flagged.missingIn2B.length} Invoices Missing in GSTR-2B:** Total ₹${flagged.missingIn2B.reduce((acc, m) => acc + (m.tax_at_risk || 0), 0).toFixed(2)} ITC at risk. You should send WhatsApp nudges to these vendors.\n` +
          `• **${flagged.blockedCredits.length} Invoices Blocked under Section 17(5):** Food, vehicles, or personal items identified.\n` +
          `• **${flagged.rcmInvoices.length} Reverse Charge (RCM) Invoices:** Foreign SaaS or advocate payments.\n` +
          `• **${flagged.mathFailures.length} Math Discrepancies:** Need manual ledger review.`;
      } else {
        const searchRes = searchLaw(q);
        toolCall = {
          toolName: "searchGstLawKnowledgeBase",
          result: searchRes.slice(0, 2)
        };
        if (searchRes.length > 0) {
          const top = searchRes[0];
          responseText = `**${top.section} — ${top.title}**\n\n` +
            `_${top.description}_\n\n` +
            `**Compliance Guidance:** ${top.founderAdvice}`;
        } else {
          responseText = "Under the CGST Act, all business purchases used in furtherance of business are eligible for ITC under Section 16(2), provided they are backed by a valid tax invoice, reflected in GSTR-2B, and not blocked under Section 17(5).";
        }
      }

      const botMsg: Message = {
        id: `assistant_${Date.now()}`,
        sender: 'assistant',
        text: responseText,
        toolCall,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };

      setMessages(prev => [...prev, botMsg]);
      setIsThinking(false);
    }, 600);
  };

  if (!copilotOpen) return null;

  return (
    <aside className={`fixed right-0 top-0 z-50 flex h-screen w-96 flex-col border-l shadow-2xl transition-colors ${
      isLight
        ? 'border-slate-200 bg-white text-slate-900'
        : 'border-[rgba(247,247,248,0.1)] bg-[#0e1015] text-[#f7f7f8]'
    }`}>
      {/* Header */}
      <div className={`flex items-center justify-between border-b px-4 py-3 ${
        isLight
          ? 'border-slate-200 bg-slate-50'
          : 'border-white/10 bg-[#131316]'
      }`}>
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded-lg ${isLight ? 'bg-cyan-100 text-cyan-700' : 'bg-cyan-500/20 text-cyan-400'}`}>
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <h3 className={`text-xs font-bold ${isLight ? 'text-slate-900' : 'text-white'}`}>GST Compliance Copilot</h3>
            <p className={`text-[10px] ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>Deterministic Tool Augmented AI</p>
          </div>
        </div>
        <button
          onClick={() => setCopilotOpen(false)}
          className={`p-1 transition rounded-lg ${isLight ? 'text-slate-400 hover:text-slate-700 hover:bg-slate-200/60' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Suggested Quick Prompt Pills */}
      <div className={`flex items-center gap-1.5 overflow-x-auto border-b px-3 py-2 text-[10px] ${
        isLight ? 'border-slate-200 bg-slate-50' : 'border-white/5 bg-[#0b0c0e]'
      }`}>
        <button
          onClick={() => handleSend("Show GSTR-3B tax summary")}
          className={`flex items-center gap-1 rounded-full px-2.5 py-1 border shrink-0 transition ${
            isLight
              ? 'bg-white border-slate-200 text-slate-700 hover:bg-slate-100 shadow-xs'
              : 'bg-white/5 border-white/5 text-slate-300 hover:bg-white/10'
          }`}
        >
          <Calculator className="h-3 w-3 text-cyan-500" />
          <span>Tax Summary</span>
        </button>
        <button
          onClick={() => handleSend("List missing invoices and ITC at risk")}
          className={`flex items-center gap-1 rounded-full px-2.5 py-1 border shrink-0 transition ${
            isLight
              ? 'bg-white border-slate-200 text-slate-700 hover:bg-slate-100 shadow-xs'
              : 'bg-white/5 border-white/5 text-slate-300 hover:bg-white/10'
          }`}
        >
          <AlertTriangle className="h-3 w-3 text-amber-500" />
          <span>At-Risk ITC</span>
        </button>
        <button
          onClick={() => handleSend("Explain Section 17(5) blocked credits")}
          className={`flex items-center gap-1 rounded-full px-2.5 py-1 border shrink-0 transition ${
            isLight
              ? 'bg-white border-slate-200 text-slate-700 hover:bg-slate-100 shadow-xs'
              : 'bg-white/5 border-white/5 text-slate-300 hover:bg-white/10'
          }`}
        >
          <BookOpen className="h-3 w-3 text-violet-500" />
          <span>Sec 17(5)</span>
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 text-xs">
        {messages.map((m) => (
          <div
            key={m.id}
            className={`flex gap-2 ${m.sender === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            {m.sender === 'assistant' && (
              <div className={`h-6 w-6 rounded-full flex items-center justify-center shrink-0 ${
                isLight
                  ? 'bg-cyan-100 border border-cyan-300 text-cyan-700'
                  : 'bg-cyan-950 border border-cyan-800 text-cyan-400'
              }`}>
                <Bot className="h-3.5 w-3.5" />
              </div>
            )}
            <div
              className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 leading-relaxed ${
                m.sender === 'user'
                  ? 'bg-cyan-600 text-white font-medium shadow-sm'
                  : isLight
                  ? 'bg-slate-100 text-slate-900 border border-slate-200 shadow-xs'
                  : 'bg-[#18191f] text-slate-200 border border-white/5'
              }`}
            >
              {m.toolCall && (
                <div className={`mb-2 flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-mono border ${
                  isLight
                    ? 'bg-cyan-50 text-cyan-800 border-cyan-200'
                    : 'bg-black/40 text-cyan-300 border border-cyan-500/20'
                }`}>
                  <span>🛠️ Called Tool: {m.toolCall.toolName}()</span>
                </div>
              )}
              <div className={`whitespace-pre-wrap ${isLight && m.sender === 'assistant' ? 'text-slate-900' : ''}`}>{m.text}</div>
              <div
                className={`mt-1 text-[9px] ${
                  m.sender === 'user'
                    ? 'text-white/80 text-right'
                    : isLight
                    ? 'text-slate-500'
                    : 'text-slate-500'
                }`}
              >
                {m.timestamp}
              </div>
            </div>
            {m.sender === 'user' && (
              <div className={`h-6 w-6 rounded-full flex items-center justify-center shrink-0 ${
                isLight
                  ? 'bg-cyan-600 text-white'
                  : 'bg-white/10 text-white'
              }`}>
                <UserIcon className="h-3.5 w-3.5" />
              </div>
            )}
          </div>
        ))}
        {isThinking && (
          <div className={`flex items-center gap-2 text-xs ${isLight ? 'text-slate-600' : 'text-slate-400'}`}>
            <Sparkles className="h-3.5 w-3.5 animate-spin text-cyan-500" />
            <span>Evaluating deterministic models & tax tables...</span>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className={`border-t p-3 ${isLight ? 'border-slate-200 bg-slate-50' : 'border-white/10 bg-[#131316]'}`}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSend();
          }}
          className="flex items-center gap-2"
        >
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about tax calculations, 2B recon, or GST law..."
            className={`flex-1 rounded-xl border px-3 py-2 text-xs focus:outline-none transition ${
              isLight
                ? 'border-slate-300 bg-white text-slate-900 placeholder-slate-400 focus:border-cyan-500'
                : 'border-white/10 bg-[#0e1015] text-white placeholder-slate-500 focus:border-cyan-500'
            }`}
          />
          <button
            type="submit"
            disabled={!input.trim()}
            className="rounded-xl bg-cyan-600 p-2 text-white hover:bg-cyan-500 disabled:opacity-40 transition shadow-sm"
          >
            <Send className="h-4 w-4" />
          </button>
        </form>
      </div>
    </aside>
  );
};
